import React, { useState, useEffect, useMemo, useRef } from 'react';
import { fetchLinearIssues, createDraftsFromLinear } from '../../api/changelog';
import { AUDIENCES } from '../../constants/changelog_constants';

const AUDIENCE_FILTERS = AUDIENCES.filter(a => a !== 'admin');
const STICKY_STATUS_OPTIONS = ['Done', 'Released'];

const audienceLabel = a => a.charAt(0).toUpperCase() + a.slice(1);

const formatCompletedAt = iso => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const LinearIssuesModal = ({ onClose, onCreated }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filterAudience, setFilterAudience] = useState([]);
  const [filterState, setFilterState] = useState([]);
  const [creating, setCreating] = useState(false);
  const backdropMouseDownRef = useRef(false);

  useEffect(() => {
    fetchLinearIssues()
      .then(data => setIssues(data || []))
      .catch(err => {
        console.error('[Pull from Linear] fetch failed:', err, err?.response);
        const backendMsg = err?.response?.data?.error || err?.response?.data?.msg || err?.message;
        const statusInfo = err?.response?.status ? ` (HTTP ${err.response.status})` : '';
        setError(`${backendMsg || 'Failed to load Linear issues.'}${statusInfo}`);
      })
      .finally(() => setLoading(false));
  }, []);

  const stateOptions = useMemo(() => {
    const names = new Set(STICKY_STATUS_OPTIONS);
    issues.forEach(i => { if (i.state) names.add(i.state); });
    return [...names].sort();
  }, [issues]);

  const filtered = useMemo(() => {
    return issues.filter(issue => {
      if (filterAudience.length && !filterAudience.some(a => (issue.audiences || []).includes(a))) return false;
      if (filterState.length && !filterState.includes(issue.state)) return false;
      return true;
    });
  }, [issues, filterAudience, filterState]);

  const toggleSelect = id => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAudienceFilter = a => {
    setFilterAudience(prev =>
      prev.includes(a) ? prev.filter(v => v !== a) : [...prev, a]
    );
  };

  const toggleStateFilter = s => {
    setFilterState(prev =>
      prev.includes(s) ? prev.filter(v => v !== s) : [...prev, s]
    );
  };

  const handleCreate = async () => {
    if (!selected.size) return;
    setCreating(true);
    setError(null);
    try {
      const result = await createDraftsFromLinear([...selected]);
      onCreated(result);
      onClose();
    } catch {
      setError('Failed to create drafts.');
      setCreating(false);
    }
  };

  return (
    <div
      className='cl-linear-backdrop'
      onMouseDown={e => { if (e.target === e.currentTarget) backdropMouseDownRef.current = true; }}
      onMouseUp={e => {
        if (e.target === e.currentTarget && backdropMouseDownRef.current) onClose();
        backdropMouseDownRef.current = false;
      }}
    >
      <div className='cl-linear-modal'>
        <div className='cl-linear-header'>
          <h2 className='cl-linear-title'>Pull from Linear</h2>
          <button className='cl-linear-close' onClick={onClose}>×</button>
        </div>

        <div className='cl-linear-subtitle'>
          Released or Done issues with the <code>Feature</code> label from the last 30 days,
          excluding ones already drafted.
        </div>

        <div className='cl-linear-filterbar'>
          <span className='cl-linear-filter-label'>Audience:</span>
          {AUDIENCE_FILTERS.map(a => (
            <button
              key={a}
              className={`cl-linear-filter-pill${filterAudience.includes(a) ? ' active' : ''}`}
              onClick={() => toggleAudienceFilter(a)}
            >
              {audienceLabel(a)}
            </button>
          ))}
          {filterAudience.length > 0 && (
            <button className='cl-linear-filter-clear' onClick={() => setFilterAudience([])}>
              Clear
            </button>
          )}
        </div>

        {stateOptions.length > 0 && (
          <div className='cl-linear-filterbar'>
            <span className='cl-linear-filter-label'>Status:</span>
            {stateOptions.map(s => (
              <button
                key={s}
                className={`cl-linear-filter-pill${filterState.includes(s) ? ' active' : ''}`}
                onClick={() => toggleStateFilter(s)}
              >
                {s}
              </button>
            ))}
            {filterState.length > 0 && (
              <button className='cl-linear-filter-clear' onClick={() => setFilterState([])}>
                Clear
              </button>
            )}
          </div>
        )}

        <div className='cl-linear-body'>
          {loading && <div className='cl-linear-loading'>Loading Linear issues...</div>}
          {error && <div className='cl-linear-error'>{error}</div>}

          {!loading && !error && filtered.length === 0 && (
            <div className='cl-linear-empty'>
              {issues.length === 0
                ? 'No qualifying Linear issues found.'
                : 'No issues match the selected audience filters.'}
            </div>
          )}

          {!loading && !error && filtered.map(issue => {
            const isSelected = selected.has(issue.id);
            return (
              <label key={issue.id} className={`cl-linear-row${isSelected ? ' selected' : ''}`}>
                <input
                  type='checkbox'
                  className='cl-linear-checkbox'
                  checked={isSelected}
                  onChange={() => toggleSelect(issue.id)}
                />
                <div className='cl-linear-row-body'>
                  <div className='cl-linear-row-meta'>
                    <span className='cl-linear-identifier'>{issue.identifier}</span>
                    {(issue.audiences || []).map(a => (
                      <span key={a} className='changelog-audience-badge'>{audienceLabel(a)}</span>
                    ))}
                    {issue.category && (
                      <span className={`changelog-category-badge changelog-category-badge--${issue.category}`}>
                        {issue.category.charAt(0).toUpperCase() + issue.category.slice(1)}
                      </span>
                    )}
                    {issue.team && <span className='cl-linear-team'>{issue.team}</span>}
                    {issue.completedAt && (
                      <span className='cl-linear-date'>{formatCompletedAt(issue.completedAt)}</span>
                    )}
                  </div>
                  <div className='cl-linear-row-title'>{issue.title}</div>
                  {issue.description && (
                    <div className='cl-linear-row-desc'>
                      {issue.description.slice(0, 200)}{issue.description.length > 200 ? '…' : ''}
                    </div>
                  )}
                  <div className='cl-linear-row-links'>
                    <a href={issue.url} target='_blank' rel='noopener noreferrer'
                       onClick={e => e.stopPropagation()}>
                      Open in Linear ↗
                    </a>
                    {issue.pr_url && (
                      <a href={issue.pr_url} target='_blank' rel='noopener noreferrer'
                         onClick={e => e.stopPropagation()}>
                        PR ↗
                      </a>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className='cl-linear-footer'>
          <div className='cl-linear-count'>
            {selected.size} selected
          </div>
          <div className='cl-linear-actions'>
            <button className='cl-linear-btn' onClick={onClose} disabled={creating}>Cancel</button>
            <button
              className='cl-linear-btn cl-linear-btn--primary'
              onClick={handleCreate}
              disabled={!selected.size || creating}
            >
              {creating ? 'Creating drafts...' : `Create ${selected.size} draft${selected.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinearIssuesModal;
